import { useState } from 'react'

import { UpdateLocationRequestDTO } from '@/core/api/dtos/request/location/update'
import { Location } from '@/core/api/entities/location'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePatchLocation } from '@/core/hooks/useLocation'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

export const useLocationUpdate = (onSuccess?: () => void) => {
	const { t } = useTranslation('common')
	const [selectedLocation, setSelectedLocation] = useState<Location | null>(
		null
	)
	const [isModalVisible, setIsModalVisible] = useState(false)

	const { showNotification } = useNotificationContext()

	const { mutate: patchLocation, isPending: isUpdating } = usePatchLocation({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message: t('entities.location.update.success.title'),
					description: t('entities.location.update.success.description', {
						name: selectedLocation?.name || '',
					}),
				})
				closeUpdateModal()
				onSuccess?.()
			},
			onError: () => {
				showNotification({
					type: 'error',
					message: t('entities.location.update.error.title'),
					description: t('entities.location.update.error.description', {
						name: selectedLocation?.name || '',
					}),
				})
			},
		},
	})

	const openUpdateModal = (location: Location) => {
		setSelectedLocation(location)
		setIsModalVisible(true)
	}

	const closeUpdateModal = () => {
		setIsModalVisible(false)
		setSelectedLocation(null)
	}

	const handleUpdateLocation = (data: UpdateLocationRequestDTO) => {
		if (selectedLocation) {
			patchLocation({
				locationId: selectedLocation.id,
				data,
			})
		}
	}

	return {
		selectedLocation,
		isModalVisible,
		isUpdating,
		openUpdateModal,
		closeUpdateModal,
		handleUpdateLocation,
	}
}
